import * as React from 'react';

import { Flex, Menu } from 'antd';
import { useLocation } from 'react-router-dom';

import {
  AntdRouterLink,
  CompactLogo,
  DatabaseOutlinedIcon,
  ExpandedLogo,
  HomeOutlinedIcon,
  PercentageOutlinedIcon,
  SettingOutlinedIcon,
  ShopOutlinedIcon,
  ShoppingCartOutlinedIcon,
  TeamOutlinedIcon,
  ToolOutlinedIcon,
} from 'components';
import { PATHNAME } from 'utils';

const {
  DEFAULT: { SLUG_SEPARATOR: DEFAULT_SLUG_SEPARATOR },
  LOCAL: {
    HOME: { BASE_URL: HOME_BASE_URL },
    PRODUCTS: { BASE_URL: PRODUCTS_BASE_URL },
    CATEGORIES: { BASE_URL: CATEGORIES_BASE_URL },
    TAGS: { BASE_URL: TAGS_BASE_URL },
    BRANDS: { BASE_URL: BRANDS_BASE_URL },
    CATALOGS: { BASE_URL: CATALOGS_BASE_URL },
    ATTRIBUTES: { BASE_URL: ATTRIBUTES_BASE_URL },
    PACKAGING_MODELS: { BASE_URL: PACKAGING_MODELS_BASE_URL },
    PACKAGES: { BASE_URL: PACKAGES_BASE_URL },
    PURCHASES: { BASE_URL: PURCHASES_BASE_URL },
    PAYMENT_GATEWAYS: { BASE_URL: PAYMENT_GATEWAYS_BASE_URL },
    SHIPPING_CONFIGS: { BASE_URL: SHIPPING_CONFIG_BASE_URL },
    USERS: { BASE_URL: USERS_BASE_URL },
    ROLES: { BASE_URL: ROLES_BASE_URL },
    PRODUCTS_COMMENTS: { BASE_URL: PRODUCTS_COMMENTS_BASE_URL },
    DISCOUNT_CODES: { BASE_URL: DISCOUNT_CODES_BASE_URL },
    SPECIAL_OFFERS: { BASE_URL: SPECIAL_OFFERS_BASE_URL },
    PRODUCTS_REPORT: { BASE_URL: PRODUCTS_REPORT_BASE_URL },
    MARKETING: { BASE_URL: MARKETING_BASE_URL },
    PRODUCTS_EDIT: { BASE_URL: PRODUCTS_EDIT_BASE_URL },
    SEO_SETTING: { BASE_URL: SEO_SETTING_BASE_URL },
    WATERMARK_SETTING: { BASE_URL: WATERMARK_SETTING_BASE_URL },
    PUBLIC_NOTICES: { BASE_URL: PUBLIC_NOTICES_BASE_URL },
    PAGE_BUILDER: { BASE_URL: PAGE_BUILDER_BASE_URL },
    PAGE_BUILDER_FOOTER: { BASE_URL: PAGE_BUILDER_FOOTER_BASE_URL },
  },
} = PATHNAME;

type PrivateSidebarProps = Partial<Record<'showLogo' | 'collapsed', boolean>>;

const PrivateSidebar = ({
  showLogo = true,
  collapsed,
}: PrivateSidebarProps) => {
  const { pathname } = useLocation();

  const splitedPath = pathname.split(DEFAULT_SLUG_SEPARATOR).filter(Boolean);
  const selectedKeys: string[] = splitedPath.length
    ? splitedPath.map(
        (_, index) =>
          `${DEFAULT_SLUG_SEPARATOR}${splitedPath.slice(0, index + 1).join(DEFAULT_SLUG_SEPARATOR)}${DEFAULT_SLUG_SEPARATOR}`,
      )
    : [DEFAULT_SLUG_SEPARATOR];

  const defaultIconProps: React.ComponentPropsWithoutRef<'span'> = {
    style: {
      fontSize: '18px',
      marginInlineStart: '0.25rem',
    },
  };

  return (
    <React.Fragment>
      {showLogo ? (
        <Flex justify='center' align='center' className='py-4'>
          {React.createElement(collapsed ? CompactLogo : ExpandedLogo)}
        </Flex>
      ) : null}
      <Menu
        mode='inline'
        className='py-4'
        selectedKeys={selectedKeys}
        items={[
          {
            key: HOME_BASE_URL,
            icon: <HomeOutlinedIcon {...defaultIconProps} />,
            label: <AntdRouterLink to={HOME_BASE_URL}>داشبورد</AntdRouterLink>,
          },
          {
            key: '/products-management/',
            icon: <ShopOutlinedIcon {...defaultIconProps} />,
            label: 'مدیریت محصول‌ها',
            children: [
              {
                key: PRODUCTS_BASE_URL,
                label: (
                  <AntdRouterLink to={PRODUCTS_BASE_URL}>
                    محصول‌ها
                  </AntdRouterLink>
                ),
              },
              {
                key: CATEGORIES_BASE_URL,
                label: (
                  <AntdRouterLink to={CATEGORIES_BASE_URL}>
                    دسته‌بندی‌ها
                  </AntdRouterLink>
                ),
              },
              {
                key: TAGS_BASE_URL,
                label: (
                  <AntdRouterLink to={TAGS_BASE_URL}>برچسب‌ها</AntdRouterLink>
                ),
              },
              {
                key: BRANDS_BASE_URL,
                label: (
                  <AntdRouterLink to={BRANDS_BASE_URL}>برندها</AntdRouterLink>
                ),
              },
              {
                key: CATALOGS_BASE_URL,
                label: (
                  <AntdRouterLink to={CATALOGS_BASE_URL}>
                    کاتالوگ‌ها
                  </AntdRouterLink>
                ),
              },
              {
                type: 'divider',
              },
              {
                key: ATTRIBUTES_BASE_URL,
                label: (
                  <AntdRouterLink to={ATTRIBUTES_BASE_URL}>
                    فیلد‌ها
                  </AntdRouterLink>
                ),
              },
              {
                key: PACKAGING_MODELS_BASE_URL,
                label: (
                  <AntdRouterLink to={PACKAGING_MODELS_BASE_URL}>
                    بسته‌بندی‌ها
                  </AntdRouterLink>
                ),
              },
              {
                key: PACKAGES_BASE_URL,
                label: <span>پکیج‌ها</span>,
                disabled: true,
              },
            ],
          },
          {
            key: '/purchases-management/',
            icon: <ShoppingCartOutlinedIcon {...defaultIconProps} />,
            label: 'مدیریت سفارش‌ها',
            children: [
              {
                key: PURCHASES_BASE_URL,
                label: (
                  <AntdRouterLink to={PURCHASES_BASE_URL}>
                    سفارش‌ها
                  </AntdRouterLink>
                ),
              },
              {
                key: PAYMENT_GATEWAYS_BASE_URL,
                label: (
                  <AntdRouterLink to={PAYMENT_GATEWAYS_BASE_URL}>
                    درگاه‌های پرداخت
                  </AntdRouterLink>
                ),
              },
              {
                key: SHIPPING_CONFIG_BASE_URL,
                label: (
                  <AntdRouterLink to={SHIPPING_CONFIG_BASE_URL}>
                    هزینه ارسال
                  </AntdRouterLink>
                ),
              },
            ],
          },
          {
            key: '/users-management/',
            icon: <TeamOutlinedIcon {...defaultIconProps} />,
            label: 'مدیریت کاربر‌ها',
            children: [
              {
                key: USERS_BASE_URL,
                label: (
                  <AntdRouterLink to={USERS_BASE_URL}>کاربرها</AntdRouterLink>
                ),
              },
              {
                key: ROLES_BASE_URL,
                label: (
                  <AntdRouterLink to={ROLES_BASE_URL}>دسترسی‌ها</AntdRouterLink>
                ),
              },
              {
                key: PRODUCTS_COMMENTS_BASE_URL,
                label: (
                  <AntdRouterLink to={PRODUCTS_COMMENTS_BASE_URL}>
                    دیدگاه کاربرها
                  </AntdRouterLink>
                ),
              },
            ],
          },
          {
            key: '/discount-management/',
            icon: <PercentageOutlinedIcon {...defaultIconProps} />,
            label: 'مدیریت تخفیف‌ها',
            children: [
              {
                key: DISCOUNT_CODES_BASE_URL,
                label: (
                  <AntdRouterLink to={DISCOUNT_CODES_BASE_URL}>
                    کد تخفیف‌ها
                  </AntdRouterLink>
                ),
              },
              {
                key: SPECIAL_OFFERS_BASE_URL,
                label: (
                  <AntdRouterLink to={SPECIAL_OFFERS_BASE_URL}>
                    تخفیف‌های ویژه
                  </AntdRouterLink>
                ),
              },
            ],
          },
          {
            key: '/tools-management/',
            icon: <ToolOutlinedIcon {...defaultIconProps} />,
            label: 'مدیریت ابزارها',
            children: [
              {
                key: PRODUCTS_REPORT_BASE_URL,
                label: (
                  <AntdRouterLink to={PRODUCTS_REPORT_BASE_URL}>
                    گزارش محصول‌ها
                  </AntdRouterLink>
                ),
              },
              {
                key: PRODUCTS_EDIT_BASE_URL,
                label: (
                  <AntdRouterLink to={PRODUCTS_EDIT_BASE_URL}>
                    ویرایش محصول‌ها
                  </AntdRouterLink>
                ),
              },
              {
                key: MARKETING_BASE_URL,
                label: <span>بازاریابی</span>,
                disabled: true,
              },
            ],
          },
          {
            key: '/settings/',
            icon: <SettingOutlinedIcon {...defaultIconProps} />,
            label: 'تنظیمات',
            children: [
              {
                key: '/page-builder-management/',
                label: 'صفحه‌ساز',
                children: [
                  {
                    key: PAGE_BUILDER_BASE_URL,
                    label: (
                      <AntdRouterLink to={PAGE_BUILDER_BASE_URL}>
                        صفحه اصلی
                      </AntdRouterLink>
                    ),
                  },
                  {
                    key: PAGE_BUILDER_FOOTER_BASE_URL,
                    label: (
                      <AntdRouterLink to={PAGE_BUILDER_FOOTER_BASE_URL}>
                        فوتر
                      </AntdRouterLink>
                    ),
                  },

                  {
                    key: PUBLIC_NOTICES_BASE_URL,
                    label: (
                      <AntdRouterLink to={PUBLIC_NOTICES_BASE_URL}>
                        اطلاع‌رسانی
                      </AntdRouterLink>
                    ),
                  },
                ],
              },
              {
                key: SEO_SETTING_BASE_URL,
                label: <span>سئو</span>,
                disabled: true,
              },
              {
                key: WATERMARK_SETTING_BASE_URL,
                label: (
                  <AntdRouterLink to={WATERMARK_SETTING_BASE_URL}>
                    نهان‌نگاری
                  </AntdRouterLink>
                ),
              },
            ],
          },
        ]}
      />
    </React.Fragment>
  );
};

export { PrivateSidebar };
